import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Fade,
  Chip,
} from '@mui/material';
import { useState } from 'react';

import { UploadStreamResponse } from '@/types/document';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import { CheckCircleIcon } from '@/assets/icons/CheckCircleIcon';
import { ClockIcon } from '@/assets/icons/ClockIcon';
import { XCircleIcon } from '@/assets/icons/XCircleIcon';
import { useDocumentUploadV2 } from '@/hooks/useDocumentUploadV2';

interface StreamEvent {
  id: string;
  timestamp: Date;
  status: UploadStreamResponse['status'];
  message: string;
  progress?: number;
}

interface DocumentUploadV2StreamProps {
  documentId: string;
  documentData?: {
    original_name?: string;
    created_by?: string;
    status?: string;
  };
  locationIds?: number[];
  dataType?: string;
  onUploadComplete?: () => void;
  onClose?: () => void;
}

const DocumentUploadV2Stream: React.FC<DocumentUploadV2StreamProps> = ({
  documentId,
  documentData,
  locationIds,
  dataType,
  onUploadComplete,
  onClose,
}) => {
  const [streamEvents, setStreamEvents] = useState<StreamEvent[]>([]);
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  const { isUploading, cancelUpload } = useDocumentUploadV2({
    onProgress: (response: UploadStreamResponse) => {
      const newEvent: StreamEvent = {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        status: response.status,
        message: response.message,
        progress: response.progress,
      };

      setStreamEvents(prev => [...prev, newEvent]);

      if (response.progress !== undefined) {
        setCurrentProgress(response.progress * 100);
      }
    },
    onComplete: (response: UploadStreamResponse) => {
      const newEvent: StreamEvent = {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        status: response.status,
        message: response.message,
      };

      setStreamEvents(prev => [...prev, newEvent]);
      setCurrentProgress(100);
      setIsComplete(true);
      onUploadComplete?.();
    },
    onError: (errorMessage: string) => {
      const newEvent: StreamEvent = {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        status: 'error',
        message: errorMessage,
      };

      setStreamEvents(prev => [...prev, newEvent]);
    },
  });

  const handleCancel = () => {
    cancelUpload();
    setStreamEvents([]);
    setCurrentProgress(0);
    setIsComplete(false);
  };

  const getEventIcon = (status: UploadStreamResponse['status']) => {
    switch (status) {
    case 'starting':
    case 'validating':
    case 'processing':
      return <ClockIcon sx={{ color: 'primary.main' }} />;
    case 'completed':
      return <CheckCircleIcon sx={{ color: 'success.main' }} />;
    case 'error':
      return <XCircleIcon sx={{ color: 'error.main' }} />;
    default:
      return <ClockIcon sx={{ color: 'grey.500' }} />;
    }
  };

  const getStatusColor = (status: UploadStreamResponse['status']) => {
    switch (status) {
    case 'starting':
      return 'info';
    case 'validating':
      return 'warning';
    case 'processing':
      return 'primary';
    case 'completed':
      return 'success';
    case 'error':
      return 'error';
    default:
      return 'default';
    }
  };

  const needsProcessing =
    documentData?.status === 'NEW' ||
    documentData?.status === 'FAILED' ||
    (!isComplete && documentData?.status === 'PROCESSED');

  if (!needsProcessing && !isUploading && streamEvents.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mb: 2,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          AI Document Processing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Process this document with AI to extract data and enable advanced features
        </Typography>

        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Processing: {Math.round(currentProgress)}%
              </Typography>
              <Chip label="Processing" color="primary" variant="outlined" size="small" />
            </Box>
            <LinearProgress
              variant={currentProgress > 0 ? 'determinate' : 'indeterminate'}
              value={currentProgress}
              sx={{ mb: 2 }}
            />
            <LoadingButton size="small" variant="outlined" onClick={handleCancel} color="error">
              Cancel Processing
            </LoadingButton>
          </Box>
        )}

        {isComplete && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Document processing completed successfully! The page will refresh automatically.
          </Alert>
        )}
      </Box>

      {streamEvents.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Processing Events
          </Typography>
          <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
            <List dense>
              {streamEvents.map((event, index) => (
                <Fade in timeout={300} key={event.id}>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>{getEventIcon(event.status)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{event.message}</Typography>
                          <Chip
                            label={event.status}
                            color={getStatusColor(event.status)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={event.timestamp.toLocaleTimeString()}
                    />
                  </ListItem>
                </Fade>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {onClose && (isComplete || (!isUploading && streamEvents.length > 0)) && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton size="small" variant="text" onClick={onClose}>
            Close
          </LoadingButton>
        </Box>
      )}
    </Paper>
  );
};

export default DocumentUploadV2Stream;
